import { reflect, type OptionsOf } from '../identity/options.ts';
import { isArray, isObject, isPresent, isString } from '../identity/type_guards.ts';
import type { Frequency, Scalar, Time } from '../units.ts';
import { check } from './core.ts';
import { EnvMode, IsBrowser, isDebugContext, IsNode } from './env.ts';
import { __registerGlobalInspect } from './inspect.ts';
import {
  formatObject,
  formatValue,
  ignoreObjectInLog,
  logCallSite,
  LogEntryBuilder,
} from './log_format.ts';
import { Style, type LogStyle, type StyleData } from './log_style.ts';
export * from './log_format.ts';
export { Style } from './log_style.ts';

const originalLog = console.log;
const originalWarn = console.warn;
const originalError = console.error;

const debugLogToConsole = false as boolean;

const logToConsole = debugLogToConsole
  ? (format: string, ...args: unknown[]) => {
      originalLog([format, ...args].join('|'));
    }
  : originalLog;

export function logError(message?: string) {
  return (err: unknown) => {
    if (err instanceof Error) {
      originalWarn(message ?? 'Unexpected error.', err.message, err);
    } else {
      originalError(message ?? 'Unexpected object thrown as error.', err);
    }
  };
}

let mayLog = true;

export function setMayLog(may: boolean): void {
  mayLog = may;
}

export function maybeLog(message: string, ...args: unknown[]): void {
  if (mayLog) {
    logToConsole(message, ...args);
  }
}

if (IsBrowser) {
  // IsBrowser() === true) {
  ignoreObjectInLog(window, 'window');
  ignoreObjectInLog(document, 'document');
} else if (IsNode) {
  // IsNode() === true) {
  ignoreObjectInLog(globalThis, 'global');
}

interface ILogState {
  canSample(): boolean;
  didSample(): void;
  // Number of calls to `canSample()` since the most recent call to `didSample()`.
  get skippedSamples(): number;
  log(prefixLength: number, sliceLength: number, format: string, ...args: unknown[]): void;
}

const defaultLimiter: ILogState = {
  canSample: () => true,
  didSample: () => undefined,
  skippedSamples: 1,
  // TODO: When this signature didn't match, TS didn't complain. Why?
  log: (_prefixLength: number, _sliceLength: number, format: string, ...args) => {
    logToConsole(format, ...args);
  },
};

class LogState implements ILogState {
  private static stateMap: Record<symbol, ILogState | undefined> = {};

  static from(options?: LogOptions): ILogState {
    if (!options?.logId) {
      check(
        options?.rate === undefined && options?.omitDuplicates === undefined,
        'logging `rate` and `omitDuplicates` require `logId`',
      );
      return defaultLimiter;
    }
    let state = LogState.stateMap[options.logId];
    if (!state) {
      state = new LogState(options);
      LogState.stateMap[options.logId] = state;
    }
    return state;
  }

  private readonly minDelay: number;
  private lastSample = 0;

  private readonly omitDuplicates: boolean;
  private lastLog = '';

  skippedSamples = 0;

  constructor(options: LogOptions) {
    if (!options.rate) {
      this.minDelay = 0;
      // TODO: predicate functions for units and quantities.
    } else if (options.rate.units.quantity.label === 'time') {
      this.minDelay = 1000 * options.rate.raw;
    } else {
      this.minDelay = 1000 / options.rate.raw;
    }
    this.omitDuplicates = options.omitDuplicates ?? false;
  }

  canSample() {
    this.skippedSamples += 1;
    return Date.now() - this.lastSample >= this.minDelay;
  }

  didSample() {
    this.skippedSamples = 0;
    this.lastSample = Date.now();
  }

  log(prefixLength: number, sliceLength: number, format: string, ...args: unknown[]) {
    if (this.omitDuplicates) {
      // TODO: A better approach may be to compose the skip count in this function.
      // Ignore the `prefixLength` of format string and `sliceLength` of args, which contain the
      // skip count.
      const logProxy = [format.slice(prefixLength), ...args.slice(sliceLength)].join('');
      if (logProxy === this.lastLog) {
        return;
      }
      // logToConsole(
      //   `prefixLength:${prefixLength}\nlast:\n%s\ncurrent:\n%s\n`,
      //   this.lastLog.replace(/%/g, ''),
      //   logProxy.replace(/%/g, '')
      // );
      this.lastLog = logProxy;
    }
    this.didSample();
    logToConsole(format, ...args);
  }
}

export type LogValues = { [label: string]: unknown } | readonly unknown[] | string;

const inlineSeparator = ' ';
const newlineSeparator = '\n';

// TODO: StringEnum once it's included in core.
type Level = 'info' | 'warn' | 'error';

type CommonSettings = {
  level: Level;
  density: 'inline' | 'collapsed' | 'expanded';
  defaultStyle?: StyleData;
  verbosity: 'terse' | 'normal' | 'max';
  // The depth at which to unpack objects and pretty-format their members. Default is 1.
  prettyDepth: number;
  // The number of elements of a container to unpack.
  prettyBreadth: number;
};

type LogSettings = CommonSettings & {
  indent: number;

  // Whether to emit the file and line number of the log statement.
  callSite: boolean;
  calleeDepth: number;
  // The remaining options are stateful with respect to calls with the same _defined_ `logId`.
  logId?: symbol;
  // Do not emit more than one log message per `rate` (Time is converted to 1/Time).
  rate?: Scalar<Frequency> | Scalar<Time>;
  // Do not emit consecutive identical messages.
  // Note: Only supported for `density === 'inline'`.
  omitDuplicates?: boolean;
  // Print count of number of skipped messages.
  emitSkips: boolean;
};

const logSettings = reflect<LogSettings>().withDefaults({
  level: 'info',
  density: 'inline',
  // defaultStyle: Style.default,
  verbosity: 'normal',
  prettyDepth: 4,
  prettyBreadth: 10,
  indent: 0,
  callSite: true, // IsBrowser,
  calleeDepth: 0,
  emitSkips: true,
});
export type LogOptions = OptionsOf<typeof logSettings>;

const defaultStyleByLevel: Partial<Record<Level, StyleData>> = {
  info: Style.console,
  warn: Style.warn,
  error: Style.error,
};

const terseSettings: Partial<LogOptions> = {
  prettyDepth: 2,
  prettyBreadth: 2,
  callSite: false,
};

const maxSettings: Partial<LogOptions> = {
  prettyDepth: Number.MAX_SAFE_INTEGER,
  prettyBreadth: Number.MAX_SAFE_INTEGER,
  indent: 2,
};

type FormatSettings = CommonSettings & {
  // If true, logFormat clears the builder after constructing the heading.
  // The heading is always returned, so the caller can use this to manipulate the heading
  // for final output composition.
  clearHeading: boolean;
  contentSeparator: string;
};

const formatSettings = reflect<FormatSettings>().withDefaults({
  level: 'info',
  density: 'inline',
  // defaultStyle: Style.default,
  verbosity: 'normal',
  prettyDepth: 4,
  prettyBreadth: 10,
  clearHeading: false,
  contentSeparator: inlineSeparator,
});
export type FormatOptions = OptionsOf<typeof formatSettings>;

// Identical to Array.join(' '), except that the space is skipped if the first non-format character
// after the break is a backspace ('\b'). In the future, we could support delete character for next
// space.
// Note: Instead of this idiom, maybe default to only putting spaces between strings, and not
// between strings and other types. That doesn't seem exactly right either.
function joinSpaced(...rest: string[]) {
  // Default implementation:
  //   return entries.join(' ');
  return rest.reduce((pre, cur, index) => {
    if (index === 0) {
      return cur;
    }
    let i = 0;
    while (i < cur.length && cur[i] === '%') {
      i += 2;
    }
    if (i < cur.length && cur[i] === '\b') {
      return pre.concat(cur.slice(0, i), cur.slice(i + 1));
    } else {
      return pre.concat(' ', cur);
    }
  });
}

export function formatLog(
  b: LogEntryBuilder,
  header?: string,
  values?: LogValues,
  options?: FormatOptions,
) {
  options ??= {};
  if (options.verbosity === 'terse') {
    options = { ...terseSettings, ...options };
  } else if (options.verbosity === 'max') {
    options = { ...maxSettings, ...options };
  }
  const settings = formatSettings.apply(options);
  let { defaultStyle, contentSeparator } = settings;
  defaultStyle ??= Style.default;
  const prettyDepth = settings.prettyDepth - 1; // Top level is 1st pretty-print depth.
  const prettyBreadth = settings.prettyBreadth;

  // TODO(bpm): Default all of our log statements to be slightly brighter to separate from other logs.
  // TODO(bpm): Infer density based on the estimated length of the line.
  if (header !== undefined) {
    b.reset(inlineSeparator);
    if (settings.density === 'inline') {
      b.setStyled(Style.inlineHeader, header).setStyled(defaultStyle);
    } else {
      b.setStyled(Style.groupHeader, ` ${header} `.padEnd(80));
    }
  }

  // We don't need a content separator here, as every following item prefixes one.
  const heading = settings.clearHeading ? b.build(true) : b.build(false); // b.build(false, contentSeparator);

  // TODO: Fold array formatting into formatValue.
  // TODO: Make better use of %0 format specifier.
  // https://developer.chrome.com/docs/devtools/console/format-style/
  if (values === undefined) {
    // No content
  } else if (isArray(values) || isString(values)) {
    const vArray = isString(values) ? [values] : values;
    vArray.forEach((v) => {
      b.reset(contentSeparator);
      if (typeof v === 'string') {
        // Note that we treat all strings as regular text, rather than value-formatted strings.
        b.setStyled(defaultStyle, v);
      } else {
        formatValue(b, v, prettyDepth, prettyBreadth);
      }
    });
  } else {
    const vObject = values;
    const hackForceSeparator = contentSeparator === '\n';
    Object.keys(vObject).forEach((k) => {
      b.reset(contentSeparator, hackForceSeparator)
        .setStyled(Style.highlight, k)
        .setStyled(Style.subtle, ':');
      formatValue(b, vObject[k], prettyDepth, prettyBreadth);
    });
  }

  return { heading };
}

// Example usage with rate limiting:
//
// const logId = Symbol('logId'); // N.B. Not Symbol.for('logId')
// const rate = cycles(1).per(seconds(10));
//
// function frequentlyCalledFunction(...) {
//   ...
//   log('header', { value: ... }, { logId, rate, omitDuplicates: true });
export function log(context?: string | object, values?: LogValues, options?: LogOptions): void {
  if (isArray(values) && isString(values[0]) && values[0].startsWith('|')) {
    logToConsole(values[0] as string, ...values.slice(1));
    return;
  }
  const state = LogState.from(options);
  if (!state.canSample()) {
    return;
  }
  const skippedSamples = state.skippedSamples - 1;

  options ??= {};
  if (options.verbosity === 'terse') {
    options = { ...terseSettings, ...options };
  } else if (options.verbosity === 'max') {
    options = { ...maxSettings, ...options };
  }
  const settings = logSettings.apply(options);
  let { level, defaultStyle } = settings;
  defaultStyle ??= defaultStyleByLevel[level] ?? Style.default;
  const contentSeparator =
    settings.density === 'inline' && (options.indent ?? 0) === 0
      ? //!(options.verbosity === 'max' && options.indent !== 0)
        inlineSeparator
      : newlineSeparator;

  const b = new LogEntryBuilder({ indent: settings.indent });
  let prefixLength = 0;
  let sliceLength = 0;
  if (skippedSamples > 0 && settings.emitSkips) {
    b.reset(inlineSeparator)
      .setStyled(Style.skipCount, String(skippedSamples))
      .setStyled(defaultStyle);
    const [format, ...args] = b.build(false);
    prefixLength = format.length + 1; // 1 is for space from joinSpaced() below.
    sliceLength = args.length; // -1 is for the format string.
  }

  let header: string | undefined;
  if (isPresent(context)) {
    header = isString(context) ? context : context.constructor.name;
  }
  const { heading } = formatLog(b, header, values, {
    ...settings,
    defaultStyle,
    contentSeparator,
    clearHeading: true,
  });

  const caller = settings.callSite ? logCallSite(settings.calleeDepth + 1) : undefined;
  if (caller) {
    b.reset(contentSeparator).setStyled(
      Style.callSite,
      `${caller.file}:${caller.line}:${caller.column}`,
    );
  }
  const content = b.build();

  if (settings.density === 'inline') {
    state.log(
      prefixLength,
      sliceLength,
      isPresent(context) ? joinSpaced(heading[0], content[0]) : content[0],
      ...heading.slice(1),
      ...content.slice(1),
    );
  } else {
    state.didSample();
    if (settings.density === 'expanded') {
      console.group(...heading);
    } else {
      console.groupCollapsed(...heading);
    }
    logToConsole(...content);
    console.groupEnd();
  }
}

console.log = console.info = (format: string, ...data: unknown[]) => {
  log(undefined, [format, ...data], { level: 'info', calleeDepth: 1 });
};
console.warn = (format: string, ...data: unknown[]) => {
  log(undefined, [format, ...data], { level: 'warn', calleeDepth: 1 });
};
console.error = (format: string, ...data: unknown[]) => {
  log(undefined, [format, ...data], { level: 'error', calleeDepth: 1 });
};

export function simpleLog(...args: unknown[]) {
  log(undefined, args, { defaultStyle: Style.console, callSite: false });
}

export function styledLog(
  cb: (b: LogEntryBuilder) => void,
  initialStyle?: LogStyle | StyleData,
  indent?: number,
) {
  const builder = new LogEntryBuilder({ style: initialStyle, indent });
  if ((indent ?? 0) > 0) {
    builder.indent(true);
    builder.break(false);
  }
  cb(builder);
  // logToConsole.apply(undefined, builder.build());
  logToConsole(...builder.build());
}

// TODO: Consider using FormatMap from logging.ts.
export type Mapper = Partial<Record<string, (v: unknown) => unknown>>;

function jsonClassReplacer(mapper: Mapper) {
  return (_key: string, value: unknown) => {
    if (
      isObject(value) &&
      isPresent(value.constructor) &&
      value.constructor !== Object &&
      value.constructor !== Array
    ) {
      const map = mapper[value.constructor.name];
      if (map) {
        return map(value);
      } else {
        return formatObject(value) ?? `Ctor<${value.constructor.name}>`;
      }
    }
    return value;
  };
}

export function toDebugJson(value: unknown, mapper?: Mapper) {
  return JSON.stringify(value, jsonClassReplacer(mapper ?? {}), 2);
}

if (isDebugContext()) {
  __registerGlobalInspect();
  originalLog('__registerGlobalInspect');
}
originalLog('Logging initialized.', { env: EnvMode });
